import {
  Chip,
  Stack,
  Tooltip,
  tooltipClasses,
  useTheme,
  useMediaQuery,
  Typography,
  TextField,
  styled,
  Checkbox,
  FormControl,
  FormControlLabel,
} from '@mui/material';
import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

/**
 * General Layout Style
 */
export const InsidePageContainer = styled(Stack)({
  padding: '0px 15px',
  [`@media (min-width: 768px)`]: {
    margin: '0 auto',
    width: '750px',
  },
  [`@media (min-width: 992px)`]: {
    width: '970px',
  },
  [`@media (min-width: 1280px)`]: {
    width: '1280px',
  },
});

export const AlignedPageContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  minHeight: 'calc(100vh - 376px)',
  height: '100%',
  padding: '30px 0px',
  gap: '30px',
  [`@media (min-width:${theme.breakpoints.values.lg}px)`]: {
    padding: '40px 0px',
    gap: '40px',
  },
}));

export const WhitePageContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: 'white',
  padding: '30px 0px',
  gap: '20px',
  [`@media (min-width:${theme.breakpoints.values.lg}px)`]: {
    padding: '40px 0px',
    gap: '25px',
  },
}));

export const RadioButtonWannaBe = ({ onClick, label, disabled }) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  return (
    <Stack
      direction={'row'}
      justifyContent={isMdUp ? 'flex-end' : 'flex-start'}
      alignItems={'center'}
      gap={'5px'}
      padding={'10px 0px'}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      sx={{ cursor: 'pointer' }}
      height={'45px'}
    >
      <RadioButtonUncheckedIcon
        sx={{
          color: '#777777',
          ...(disabled && {
            opacity: 0.2,
          }),
        }}
        fontSize="small"
      />
      <Typography
        variant={'body2'}
        sx={{
          ...(disabled && {
            opacity: 0.2,
          }),
          whiteSpace: 'nowrap',
          color: '#333333',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

export const RedLinkButton = ({
  onClick,
  msg,
  marginRight = null,
  leftIcon = null,
  rightIcon = null,
  hideTextOnMobile = false,
  customStyle = {},
  variant = 'link1',
  disabled = false,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
      gap={'10px'}
      sx={{ cursor: disabled ? 'default' : 'pointer', ...customStyle }}
      marginRight={marginRight}
      onClick={onClick}
    >
      {leftIcon}
      {hideTextOnMobile && isSmDown ? null : (
        <Typography
          variant={variant}
          sx={{
            textDecoration: 'underline',
            ...(disabled && {
              color: '#E2001A',
              // color: '#EC0E00',
              opacity: 0.2,
            }),
            whiteSpace: 'nowrap',
          }}
          color="primary"
          fontWeight={600}
        >
          {msg}
        </Typography>
      )}
      {rightIcon}
    </Stack>
  );
};

export const PaginationButton = ({
  prevIcon = null,
  nextIcon = null,
  onClick,
  msg,
  color = 'black',
}) => {
  return (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
      gap={'10px'}
      sx={{ cursor: 'pointer' }}
      onClick={() => onClick()}
    >
      {prevIcon}
      <Typography
        variant="body2-bold"
        sx={{ textDecoration: 'underline', color: `${color} !important` }}
      >
        {msg}
      </Typography>
      {nextIcon}
    </Stack>
  );
};

export const SdmTextfieldV5 = styled(TextField)({
  width: '90%',
  '& .MuiFormControl-root': {
    width: '95%',
  },
  '& label.Mui-focused': {
    color: `black`,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: `black`,
  },
  '& .MuiInput-underline.Mui-disabled:before': {
    borderBottomStyle: `solid`,
    borderBottomColor: '#919EAB',
  },
  '& .MuiInput-underline:before': {
    borderBottom: `2px solid black`,
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Suisse-Intl',
    fontSize: 26,
  },
  '& .MuiFormLabel-root': {
    fontFamily: 'Suisse-Intl',
    color: `black`,
    fontSize: 21,
  },
});

export const ModalTextField = styled(TextField)({
  width: '100%',
  '& .MuiFormControl-root': {
    width: '95%',
  },
  '& label.Mui-focused': {
    color: `black`,
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: `black`,
  },
  '& .MuiInput-underline.Mui-disabled:before': {
    borderBottomStyle: `solid`,
    borderBottomColor: '#919EAB',
  },
  '& .MuiInput-underline:before': {
    borderBottom: `2px solid black`,
  },
  '& .MuiInputBase-input': {
    fontFamily: 'Suisse-Intl',
    fontSize: 26,
  },
  '& .MuiFormLabel-root': {
    fontFamily: 'Suisse-Intl',
    color: `black`,
    fontSize: 21,
  },
});

/**
 * Exhibitors Card Style
 */
export const ExhibitorCardWrapper = styled('li')(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.grey[200]}`,
  width: '100%',
  padding: '20px',
  display: 'flex',
  flexDirection: 'row',
  gap: '20px',
  alignItems: 'center',
  cursor: 'pointer',
  [`@media (min-width:1024px)`]: {
    maxWidth: '858px',
    minWidth: '588px',
  },
}));

export const ExhibitorCardWrapperMobile = styled('li')(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.grey[200]}`,
  padding: '25px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '15px',
  cursor: 'pointer',
}));

export const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: '100%',
  color: 'white',
  borderRadius: '25px',
  height: '100% !important',
  maxHeight: '28px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
  },
}));

export const CustomerCompanyLogoMobile = styled('img')(() => ({
  backgroundColor: 'red',
  minWidth: '100px',
  minHeight: '100px',
  margin: 'auto',
}));

export const CustomerCompanyLogo = styled('img')(() => ({
  backgroundColor: 'red',
  minWidth: '100px',
  height: '100px',
}));

export const IconButtonContainerMobile = styled(Stack)({
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});

export const ElipsedTypography = styled(Typography)(({ maxLine = 2 }) => ({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
  WebkitLineClamp: maxLine,
  fontFamily: 'FSSiena-Regular !important',
}));

// export const StyledTooltip = {
//   backgroundColor: '#FFF',
//   color: 'grey',
//   borderRadius: 0,
// };

export const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#FFF',
    color: theme.palette.grey[800],
    borderRadius: 0,
    padding: '15px',
    boxShadow: '0px 0px 16px rgba(51,51,51, 0.15);',
    inset: 'auto auto -18px 0px',
    fontSize: '16px',
    textAlign: 'center',
  },
  [`& .${tooltipClasses.arrow}`]: {
    '&:before': {
      backgroundColor: '#FFF',
    },
  },
}));

export const StyledAddIcon = styled(AddIcon)(({ theme }) => ({
  '&.MuiSvgIcon-colorPrimary': {
    color: theme.palette.primary.main,
  },
}));

export const BackButton = ({ backFn, backMsg }) => {
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      gap={'10px'}
      sx={{ cursor: 'pointer' }}
      onClick={() => backFn()}
    >
      <ArrowBackIcon />
      <Typography variant="link1" sx={{ textDecoration: 'underline' }}>
        {backMsg}
      </Typography>
    </Stack>
  );
};

export const FolderCardWrapper = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.grey[200]}`,
  width: '100%',
  padding: '15px',
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  cursor: 'pointer',
}));

export const FolderCardWrapperMobile = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  border: `1px solid ${theme.palette.grey[200]}`,
  width: '100%',
  padding: '20px',
  display: 'flex',
  flexDirection: 'row',
  gap: '5px',
  alignItems: 'center',
  cursor: 'pointer',
  justifyContent: 'space-between',
}));

export const FolderCardCTAWrapperMobile = styled('div')(({ theme }) => ({
  width: '45px',
  height: '45px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '4px',
  border: `1px solid ${theme.palette.primary.main}`,
}));

export const ViewFoldersDialogContent = styled(Stack)(() => ({
  padding: '30px 20px',
  gap: '25px',
}));

export const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  borderRadius: '50%',
  width: '20px',
  height: '20px',
  border: `1px solid ${theme.palette.grey[800]}`,
}));

export const RoundedCheckmarkBorder = styled('div')(() => ({
  borderRadius: '50%',
  width: '100%',
  height: '100%',
}));

export const RoundedCheckmarkFill = styled('div')(({ theme }) => ({
  borderRadius: '50%',
  width: '100%',
  height: '100%',
  backgroundColor: `${theme.palette.primary.main}`,
}));

export const VisitToggleRadio = ({ checked, handleChange, style = {} }) => {
  return (
    <FormControl component="fieldset">
      <FormControlLabel
        style={style}
        onClick={(event) => event.stopPropagation()}
        control={
          <Checkbox
            disableRipple
            disableElevation
            size={'small'}
            checked={checked}
            onChange={handleChange}
            onClick={(event) => event.stopPropagation()}
            name="visit"
            style={{
              padding: 0,
              marginLeft: -13,
              borderRadius: 25,
              backgroundColor: 'transparent',
            }}
            icon={
              <RadioButtonUncheckedIcon
                disableElevation
                disableRipple
                size={'small'}
              />
            }
            checkedIcon={
              <RadioButtonCheckedIcon
                size={'small'}
                style={{ height: 20, width: 20 }}
                disableRipple
                disableElevation
              />
            }
          />
        }
      />
    </FormControl>
  );
};
